// Presentation helpers shared by the JobsFilter child components. Extracted
// verbatim from JobsFilterView so JobCard / JobDetailRow can render dates,
// flags, and Upwork enum labels without the parent re-passing them.

// Upwork's `JobDuration` enum (per their docs): WEEK / MONTH / QUARTER /
// SEMESTER / ONGOING. The dashboard renders friendly labels.
export const DURATION_LABELS = {
    WEEK: 'Less than 1 month',
    MONTH: '1 to 3 months',
    QUARTER: '3 to 6 months',
    SEMESTER: '6 months',
    ONGOING: 'More than 6 months',
};

export const EXPERIENCE_LABELS = {
    ENTRY_LEVEL: 'Entry level',
    INTERMEDIATE: 'Intermediate',
    EXPERT: 'Expert',
};

// Upwork returns `client.location.country` either as an ISO-3 code (e.g. "USA",
// "GBR") or as a display name ("United States"). Map both to a flag emoji.
const ISO3_TO_FLAG = {
    USA: '🇺🇸', GBR: '🇬🇧', AUS: '🇦🇺', CAN: '🇨🇦', DEU: '🇩🇪', FRA: '🇫🇷',
    ESP: '🇪🇸', ITA: '🇮🇹', NLD: '🇳🇱', SWE: '🇸🇪', NOR: '🇳🇴', DNK: '🇩🇰',
    FIN: '🇫🇮', POL: '🇵🇱', UKR: '🇺🇦', IND: '🇮🇳', PAK: '🇵🇰', BGD: '🇧🇩',
    PHL: '🇵🇭', IDN: '🇮🇩', VNM: '🇻🇳', CHN: '🇨🇳', JPN: '🇯🇵', KOR: '🇰🇷',
    SGP: '🇸🇬', MYS: '🇲🇾', THA: '🇹🇭', TUR: '🇹🇷', ARE: '🇦🇪', SAU: '🇸🇦',
    ISR: '🇮🇱', EGY: '🇪🇬', ZAF: '🇿🇦', NGA: '🇳🇬', KEN: '🇰🇪', BRA: '🇧🇷',
    ARG: '🇦🇷', MEX: '🇲🇽', CHL: '🇨🇱', COL: '🇨🇴', PER: '🇵🇪', NZL: '🇳🇿',
    IRL: '🇮🇪', PRT: '🇵🇹', CHE: '🇨🇭', AUT: '🇦🇹', BEL: '🇧🇪', CZE: '🇨🇿',
    HUN: '🇭🇺', ROU: '🇷🇴', RUS: '🇷🇺', BLR: '🇧🇾', GEO: '🇬🇪', ARM: '🇦🇲',
};
const NAME_TO_FLAG = {
    'United States': '🇺🇸', 'United Kingdom': '🇬🇧', Australia: '🇦🇺', Canada: '🇨🇦',
    Germany: '🇩🇪', France: '🇫🇷', Spain: '🇪🇸', Italy: '🇮🇹', Netherlands: '🇳🇱',
    Sweden: '🇸🇪', Norway: '🇳🇴', Denmark: '🇩🇰', Finland: '🇫🇮', Poland: '🇵🇱',
    Ukraine: '🇺🇦', India: '🇮🇳', Pakistan: '🇵🇰', Bangladesh: '🇧🇩', Philippines: '🇵🇭',
    Indonesia: '🇮🇩', Vietnam: '🇻🇳', China: '🇨🇳', Japan: '🇯🇵', 'South Korea': '🇰🇷',
    Singapore: '🇸🇬', Malaysia: '🇲🇾', Thailand: '🇹🇭', Turkey: '🇹🇷',
    'United Arab Emirates': '🇦🇪', 'Saudi Arabia': '🇸🇦', Israel: '🇮🇱', Egypt: '🇪🇬',
    'South Africa': '🇿🇦', Nigeria: '🇳🇬', Kenya: '🇰🇪', Brazil: '🇧🇷', Argentina: '🇦🇷',
    Mexico: '🇲🇽', Chile: '🇨🇱', Colombia: '🇨🇴', Peru: '🇵🇪', 'New Zealand': '🇳🇿',
    Ireland: '🇮🇪', Portugal: '🇵🇹', Switzerland: '🇨🇭', Austria: '🇦🇹', Belgium: '🇧🇪',
    'Czech Republic': '🇨🇿', Hungary: '🇭🇺', Romania: '🇷🇴', Russia: '🇷🇺',
};
export function flagFor(country) {
    if (!country) return '🌍';
    return ISO3_TO_FLAG[country] || NAME_TO_FLAG[country] || '🌍';
}

// `created_at` / `published_at` are stored as Unix epoch SECONDS; `posted_on`
// is a human-readable string and may be empty for feed sources. Prefer the
// string when present, otherwise scale the seconds up to milliseconds — passing
// raw seconds to `new Date()` makes it read 1779128744 as ms → Jan 1970, which
// rendered as the bogus "20588d ago".
function jobDate(job) {
    if (job.posted_on) {
        const d = new Date(job.posted_on);
        if (!isNaN(d.getTime())) return d;
    }
    const secs = job.published_at || job.created_at;
    return secs ? new Date(Number(secs) * 1000) : null;
}

export function relativeTime(job) {
    const date = jobDate(job);
    if (!date || isNaN(date.getTime())) return '—';
    const diffInSeconds = Math.floor((new Date() - date) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
}
